import { useState } from 'react';
import { toast } from 'sonner';
import { useEmailSeatCredentials, useReissueSeat, useRevokeSeat } from '@/services/queries';
import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import type { CustomerSeat } from '@/types';

/**
 * Taking a seat back, which is two different conversations.
 *
 * A seat nobody ever activated was a mistake - the wrong address, somebody who
 * never started - and the useful question is whether their portal login was a
 * mistake too. A seat somebody has worked on cannot be unmade, and the useful
 * question is whether they are getting a replacement key or losing the seat.
 *
 * Which one is asked is decided here rather than by the admin, because the
 * server decides it too: deleting a seat with history is refused outright, so
 * offering it would be offering a button that answers 409.
 */
type Choice = 'seat' | 'seat_and_user' | 'reissue';

/**
 * Which conversation this opening is having.
 *
 * `reissue` is here rather than in a dialog of its own because it ends in the
 * same place - a key on screen that has to be read to the customer once - and
 * a second component to show one string would drift from this one.
 */
export type SeatDialogIntent = 'revoke' | 'reissue';

export function RevokeSeatDialog({
  organizationId,
  seat,
  holderName,
  intent = 'revoke',
  open,
  onOpenChange,
}: {
  organizationId: string;
  seat: CustomerSeat;
  /** Null when the seat has no portal login on it - nothing to keep or delete. */
  holderName: string | null;
  intent?: SeatDialogIntent;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {/* Remounted per opening, so a half-made choice from last time is gone. */}
        <RevokeSeatBody
          organizationId={organizationId}
          seat={seat}
          holderName={holderName}
          intent={intent}
          onClose={() => {
            onOpenChange(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

function RevokeSeatBody({
  organizationId,
  seat,
  holderName,
  intent,
  onClose,
}: {
  organizationId: string;
  seat: CustomerSeat;
  holderName: string | null;
  intent: SeatDialogIntent;
  onClose: () => void;
}) {
  /**
   * `everActivated`, not `activatedAt`/`expiresAt` - every seat has both set
   * from the moment it is bought, before any device has touched it, so
   * neither can tell "never used" apart from "in use" any more.
   * `everActivated` comes from the seat's device history instead, which a
   * release marks revoked rather than deleting - so a reissued seat still
   * reads as used, and this still can't be tricked into offering to delete a
   * term the customer has already paid for. The server draws the line the
   * same way - see its SeatHasHistoryError.
   */
  const used = seat.everActivated;

  const [choice, setChoice] = useState<Choice>(used ? 'reissue' : 'seat');
  const [newKey, setNewKey] = useState<string | null>(null);

  const revoke = useRevokeSeat(organizationId);
  const reissue = useReissueSeat(organizationId);
  const emailKey = useEmailSeatCredentials(organizationId);
  const pending = revoke.isPending || reissue.isPending;

  if (newKey !== null) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>New licence key</DialogTitle>
          <DialogDescription>
            {seat.email} activates with this, and the old key no longer works
            {seat.machineFingerprintHash === null
              ? '.'
              : ' — the computer it was activated on has been released.'}
          </DialogDescription>
        </DialogHeader>

        <p className="rounded-md border border-border bg-muted px-3 py-2 font-mono text-sm break-all">
          {newKey}
        </p>

        <DialogFooter>
          <Button
            variant="outline"
            disabled={emailKey.isPending}
            onClick={() => {
              // No password here - a reissue replaces the key and leaves the
              // login alone - so the server sends the key on its own.
              emailKey.mutate(
                { seatId: seat.id },
                {
                  onSuccess: (sent) => {
                    toast.success(`Sent to ${sent.toEmail}.`);
                  },
                  onError: (error) => {
                    toast.error(error.message);
                  },
                },
              );
            }}
          >
            {emailKey.isPending ? 'Sending…' : 'Email it'}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              navigator.clipboard.writeText(newKey).then(
                () => {
                  toast.success('Copied.');
                },
                () => {
                  toast.error('Could not copy. Select the text and copy it manually.');
                },
              );
            }}
          >
            Copy
          </Button>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </>
    );
  }

  const confirm = () => {
    if (choice === 'reissue') {
      reissue.mutate(seat.id, {
        onSuccess: (result) => {
          setNewKey(result.licenseKey);
        },
        onError: (error) => {
          toast.error(error.message);
        },
      });
      return;
    }

    revoke.mutate(
      { seatId: seat.id, mode: choice },
      {
        onSuccess: () => {
          toast.success(
            choice === 'seat_and_user'
              ? `${seat.email} no longer has a seat or a login.`
              : `${seat.email}'s seat is revoked. You can issue them a new key from their row.`,
          );
          onClose();
        },
        onError: (error) => {
          toast.error(error.message);
        },
      },
    );
  };

  const who = holderName ? `${holderName} (${seat.email})` : seat.email;

  if (intent === 'reissue') {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Issue a new licence key?</DialogTitle>
          <DialogDescription>
            {who} gets a working key again
            {seat.expiresAt === null
              ? '. The term starts when they first activate it.'
              : ', on the same expiry date as before.'}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              reissue.mutate(seat.id, {
                onSuccess: (result) => {
                  setNewKey(result.licenseKey);
                },
                onError: (error) => {
                  toast.error(error.message);
                },
              });
            }}
            disabled={pending}
          >
            {pending ? 'Issuing…' : 'Issue key'}
          </Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Revoke this seat?</DialogTitle>
        <DialogDescription>
          {who} loses this licence key immediately, and any computer it is activated on stops at its
          next check.
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-2">
        {used ? (
          <>
            <ChoiceCard
              title="Issue a replacement key"
              subtitle="Same expiry date — they finish the term already bought, on a new computer."
              selected={choice === 'reissue'}
              onSelect={() => {
                setChoice('reissue');
              }}
            />
            <ChoiceCard
              title="Revoke the seat"
              subtitle="The seat is kept but goes dead. You can still issue a key later from their row."
              selected={choice === 'seat'}
              onSelect={() => {
                setChoice('seat');
              }}
            />
          </>
        ) : (
          <>
            <ChoiceCard
              title="Revoke the seat only"
              subtitle={
                holderName
                  ? 'Their portal login stays, so you can give them a key later.'
                  : 'Nobody holds this seat, so there is no login to keep.'
              }
              selected={choice === 'seat'}
              onSelect={() => {
                setChoice('seat');
              }}
            />
            <ChoiceCard
              title="Revoke the seat and delete the person"
              subtitle="Removes their portal login and signs them out everywhere. Cannot be undone."
              disabled={holderName === null}
              disabledReason="There is no portal login on this seat to delete."
              selected={choice === 'seat_and_user'}
              onSelect={() => {
                setChoice('seat_and_user');
              }}
            />
          </>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button onClick={confirm} disabled={pending}>
          {pending
            ? 'Working…'
            : choice === 'reissue'
              ? 'Issue new key'
              : choice === 'seat_and_user'
                ? 'Revoke and delete'
                : 'Revoke seat'}
        </Button>
      </DialogFooter>
    </>
  );
}

/** The selectable card IssueLicenseDialog uses for plans, for the same reason:
 *  two options with a sentence each read better than a radio list. */
function ChoiceCard({
  title,
  subtitle,
  selected,
  disabled = false,
  disabledReason,
  onSelect,
}: {
  title: string;
  subtitle: string;
  selected: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'rounded-lg border p-4 text-left transition-colors',
        disabled
          ? 'cursor-not-allowed border-input opacity-55'
          : selected
            ? 'border-primary bg-primary/5'
            : 'border-input hover:bg-accent',
      )}
    >
      <p className="text-sm font-medium">{title}</p>
      <p className="text-sm text-muted-foreground">{disabled ? disabledReason : subtitle}</p>
    </button>
  );
}
